import React, { useEffect } from "react";
import * as R from "ramda";
import { withPrefix, Script, ScriptStrategy } from "gatsby";
import { useSiteMetadata } from "../hooks/use-site-metadata";
import { useI18next } from "gatsby-plugin-react-i18next";
import { changeLanguage } from "../../config/i18n";
import i18n, { locales } from "../../config/i18n";



interface ISEO {
    locale: string;
    rawTitle?: string;
    title: string;
    description: string;
    pathname: string;
    keywords: string;
    children?: JSX.Element[];
}

const SEO: React.FC<Partial<ISEO>> = ({ locale, rawTitle, title, description, pathname, keywords, children }: Partial<ISEO>) => {
    const { t, language } = useI18next("translation", { i18n });

    useEffect(() => {
        document.documentElement.lang = locales.find((item => item.locale === locale))?.strapi || "zh";
        const cl = async () => {
            if (locale) {
                await changeLanguage(locale);
            }
        };
        cl();
    }, [locale]);

    const { locale: defaultLocale, title: defaultTitle, description: defaultDescription, keywords: defaultKeywords, image, siteUrl } = useSiteMetadata();
    let seoTitle = defaultTitle;
    if (rawTitle) {
        seoTitle = `${rawTitle} | ${t("seo.base.title")}`;
    }
    if (title) {
        seoTitle = `${t(title)} | ${t("seo.base.title")}`;
    }
    const seo = {
        locale: locale || language || defaultLocale,
        title: seoTitle,
        description: description ? t(description) : defaultDescription,
        image: `${siteUrl}${image}`,
        url: `${siteUrl}${pathname || ``}`,
        keywords: keywords ? t(keywords) : defaultKeywords,
    };

    return (
        <>
            <title>{seo.title}</title>
            <meta name="description" content={seo.description} />
            <meta name="image" content={seo.image} />
            <link rel="alternate" href={`https://www.storeberry.ai/${seo.locale || ""}`} hrefLang={seo.locale}></link>

            <meta name="keywords" content={seo.keywords}></meta>
            <meta name="robots" content="max-image-preview:large, index, follow, max-snippet:-1, max-video-preview:-1"></meta>
            <meta name="apple-mobile-web-app-capable" content="yes"></meta>
            <meta name="apple-mobile-web-app-status-bar-style" content="#c20075"></meta>
            <link rel="apple-touch-icon" sizes="180x180" href={withPrefix("images/favicon/apple-touch-icon.png")}></link>
            <link rel="icon" type="image/png" sizes="16x16" href={withPrefix("images/favicon/favicon-16x16.png")}></link>
            <link rel="icon" type="image/png" sizes="32x32" href={withPrefix("images/favicon/favicon-32x32.png")}></link>
            <link rel="mask-icon" href={withPrefix("images/favicon/safari-pinned-tab.svg")} color="#5bbad5"></link>
            <meta name="msapplication-TileColor" content="#c20075"></meta>
            <meta name="theme-color" content="rbg(116, 0, 198)"></meta>
            <meta charSet="utf-8"></meta>
            <meta name="HandheldFriendly" content="true"></meta>
            <meta property="og:title" content={seo.title}></meta>
            <meta property="og:type" content="website"></meta>
            <meta property="og:url" content={`https://www.storeberry.ai/${seo.locale || ""}`}></meta>
            <meta property="og:image" content="https://assets.storeberry.ai/hk/legacy/img/favicon/sb_logo.jpeg"></meta>
            <meta property="og:image:width" content="1024"></meta>
            <meta property="og:image:height" content="1024"></meta>
            <meta property="og:description" content={seo.description}></meta>
            <meta property="og:site_name" content={seo.title}></meta>
            <meta property="og:locale" content={seo.locale}></meta>
            <meta property="og:type" content="website"></meta>
            <meta property="article:publisher" content="https://www.facebook.com/storeberry.retail"></meta>

            <Script strategy={ScriptStrategy.postHydrate}>
                {`
                    (function(h,o,t,j,a,r){
                        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                        h._hjSettings={hjid:3434884,hjsv:6};
                        a=o.getElementsByTagName('head')[0];
                        r=o.createElement('script');r.async=1;
                        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                        a.appendChild(r);
                    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=')
                `}
            </Script>
            <Script strategy={ScriptStrategy.postHydrate}>
                {
                    `(function(c,l,a,r,i,t,y){
                        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                    })(window, document, "clarity", "script", "hoc1ljowb6");`
                }
            </Script>
            <Script strategy={ScriptStrategy.postHydrate} type="text/javascript" id="hs-script-loader" async defer src="//js-na1.hs-scripts.com/44863285.js"></Script>
            {children}
        </>
    );
};

export default SEO;
